import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { exportToFile } from '../../../services/export';
import { loadOpportunityReport } from '../../../services/reports';
import { getBuilderId } from '../../../redux/selectors/auth';
import { OpportunityReportData } from '../../../redux/types/reports';
import {
  communityExportColumns,
  communitySearchValueGetters,
  CsvColumn,
  defaultOpportunitySummary,
  getExpansionOpportunity,
  getPropertyAction,
  opportunityReportConfig as config,
  opportunityReportFileNames,
  propertyExportColumns,
  propertySearchValueGetters,
} from '../constants';
import {
  OpportunityCommunityTableRow,
  OpportunityPropertyTableRow,
  OpportunityTabKey,
} from '../types';
import { useTableState } from './useTableState.hook';

const stringifyValue = (value: string | number | null | undefined) => (value === null || value === undefined ? '' : String(value));

const buildCsv = <T extends object>(
  rows: T[],
  columns: CsvColumn<T>[],
): string => [
  columns.map((column) => stringifyValue(column.label)).join(','),
  ...rows.map((row) => columns.map((column) => stringifyValue(column.value(row))).join(',')),
].join('\n');

const useOpportunityReport = () => {
  const builderId = useSelector(getBuilderId);
  const isBuilderSelectionRequired = !builderId || builderId === 0;

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(defaultOpportunitySummary);
  const [activeTab, setActiveTab] = useState<OpportunityTabKey>(config.defaultTab);
  const [search, setSearch] = useState('');

  const communityTable = useTableState<OpportunityCommunityTableRow>(search, {
    initialOrder: config.community.order,
    initialOrderBy: config.community.orderBy,
    initialRowsPerPage: config.rowsPerPage,
    searchValueGetters: communitySearchValueGetters,
  });

  const propertyTable = useTableState<OpportunityPropertyTableRow>(search, {
    initialOrder: config.property.order,
    initialOrderBy: config.property.orderBy,
    initialRowsPerPage: config.rowsPerPage,
    searchValueGetters: propertySearchValueGetters,
  });

  const {
    page: communityPage,
    reset: resetCommunityTable,
    rowsPerPage: communityRowsPerPage,
    setPage: setCommunityPage,
    setRows: setCommunityRows,
    setSelected: setSelectedCommunities,
  } = communityTable;

  const {
    page: propertyPage,
    reset: resetPropertyTable,
    rowsPerPage: propertyRowsPerPage,
    setPage: setPropertyPage,
    setRows: setPropertyRows,
    setSelected: setSelectedProperties,
  } = propertyTable;

  const fetchReport = useCallback(async (selectedBuilderId: number) => {
    try {
      setLoading(true);
      setError('');
      const { data } = await loadOpportunityReport(selectedBuilderId);
      const reportData = data as OpportunityReportData;

      setSummary(reportData.summary);
      setSelectedCommunities([]);
      setSelectedProperties([]);
      setCommunityRows(reportData.communities.map((row, index) => ({
        ...row,
        rowId: `community-${row.externalCommunityId || row.communityName}-${index}`,
        ...getExpansionOpportunity(row.feedSpecHomesCount),
      })));
      setPropertyRows(reportData.properties.map((row, index) => ({
        ...row,
        rowId: `property-${row.externalIdentifier || row.address}-${index}`,
        ...getPropertyAction(row.status),
      })));
    } catch (loadError) {
      setSummary(defaultOpportunitySummary);
      resetCommunityTable();
      resetPropertyTable();
      setError(
        loadError?.response?.data?.message
          || loadError?.message
          || 'Failed to load opportunity report.',
      );
    } finally {
      setLoading(false);
    }
  }, [
    resetCommunityTable,
    resetPropertyTable,
    setCommunityRows,
    setPropertyRows,
    setSelectedCommunities,
    setSelectedProperties,
  ]);

  useEffect(() => {
    setSearch('');
    setCommunityPage(0);
    setPropertyPage(0);

    if (isBuilderSelectionRequired) {
      setLoading(false);
      setError('');
      setSummary(defaultOpportunitySummary);
      resetCommunityTable();
      resetPropertyTable();
      return;
    }

    fetchReport(builderId);
  }, [
    builderId,
    fetchReport,
    isBuilderSelectionRequired,
    resetCommunityTable,
    resetPropertyTable,
    setCommunityPage,
    setPropertyPage,
  ]);

  useEffect(() => {
    if (activeTab === 'communities') {
      setCommunityPage(0);
      return;
    }

    setPropertyPage(0);
  }, [activeTab, search, setCommunityPage, setPropertyPage]);

  useEffect(() => {
    setSelectedCommunities([]);
  }, [builderId, communityPage, communityRowsPerPage, search, setSelectedCommunities]);

  useEffect(() => {
    setSelectedProperties([]);
  }, [builderId, propertyPage, propertyRowsPerPage, search, setSelectedProperties]);

  const isReportEmpty = !loading && communityTable.rows.length === 0 && propertyTable.rows.length === 0;

  const refreshReport = () => {
    if (!isBuilderSelectionRequired) {
      fetchReport(builderId);
    }
  };

  const handleExportAll = () => {
    if (activeTab === 'communities') {
      const csv = buildCsv(communityTable.sorted, communityExportColumns);
      exportToFile([csv], { type: 'text/csv' }, opportunityReportFileNames.communities);
      return;
    }

    const csv = buildCsv(propertyTable.sorted, propertyExportColumns);
    exportToFile([csv], { type: 'text/csv' }, opportunityReportFileNames.properties);
  };

  const handleExportSelected = () => {
    if (activeTab === 'communities') {
      const selectedData = communityTable.rows.filter((row) => communityTable.selected.includes(row.rowId));
      const csv = buildCsv(selectedData, communityExportColumns);
      exportToFile([csv], { type: 'text/csv' }, opportunityReportFileNames.communities);
      return;
    }

    const selectedData = propertyTable.rows.filter((row) => propertyTable.selected.includes(row.rowId));
    const csv = buildCsv(selectedData, propertyExportColumns);
    exportToFile([csv], { type: 'text/csv' }, opportunityReportFileNames.properties);
  };

  return {
    activeTab,
    builderId,
    communityTable,
    error,
    handleExportAll,
    handleExportSelected,
    isBuilderSelectionRequired,
    isReportEmpty,
    loading,
    propertyTable,
    refreshReport,
    search,
    setActiveTab,
    setSearch,
    summary,
  };
};

export { useOpportunityReport };
